import moment from 'moment';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { RaceTypes, Gender } from '../../../../Component/Model/Enum/BEE123Enums';
import { DemographicGroup, SeverityLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import { raceToDemographic, formatRelativeGap } from '../Shared/deviationsCommon';

export { formatRelativeGap };

interface DemographicSlot {
    raceId: RaceTypes;
    genderId: Gender;
}

const designatedMaleSlots: DemographicSlot[] = [
    { raceId: RaceTypes.African,  genderId: Gender.Male },
    { raceId: RaceTypes.Coloured, genderId: Gender.Male },
    { raceId: RaceTypes.Indian,   genderId: Gender.Male },
];

const designatedFemaleSlots: DemographicSlot[] = [
    { raceId: RaceTypes.African,  genderId: Gender.Female },
    { raceId: RaceTypes.Coloured, genderId: Gender.Female },
    { raceId: RaceTypes.Indian,   genderId: Gender.Female },
    { raceId: RaceTypes.White,    genderId: Gender.Female },
];

const nonDesignatedSlots: DemographicSlot[] = [
    { raceId: RaceTypes.White,    genderId: Gender.Male },
    { raceId: RaceTypes.NonRSA,   genderId: Gender.Female },
    { raceId: RaceTypes.NonRSA,   genderId: Gender.Male },
];

export type SubtotalVariant = 'designatedMale' | 'designatedFemale' | 'totalDesignated' | 'nonDesignated';

export type PlanSummaryTableRow =
    | { kind: 'demographic'; label: DemographicGroup; data: DeviationsDto.PlanSummaryRowDto }
    | { kind: 'subtotal';    variant: SubtotalVariant; label: string; data: DeviationsDto.PlanSummarySubtotalRowDto };

function emitDemographicRows(slots: DemographicSlot[], rowMap: Map<string, DeviationsDto.PlanSummaryRowDto>): PlanSummaryTableRow[] {
    const out: PlanSummaryTableRow[] = [];
    slots.forEach(({ raceId, genderId }) => {
        const data: DeviationsDto.PlanSummaryRowDto | undefined = rowMap.get(`${raceId}-${genderId}`);
        if (!data) return;
        const label: DemographicGroup | undefined = raceToDemographic[raceId]?.[genderId];
        if (!label) return;
        out.push({ kind: 'demographic', label, data });
    });
    return out;
}

export function buildTableRows(occupationalLevel: DeviationsDto.PlanSummaryOccupationalLevelDto): PlanSummaryTableRow[] {
    const rowMap: Map<string, DeviationsDto.PlanSummaryRowDto> = new Map();
    occupationalLevel.demographicRows.forEach(row => rowMap.set(`${row.raceId}-${row.genderId}`, row));

    const { designatedMale, designatedFemale, totalDesignated, nonDesignated } = occupationalLevel.subtotals;

    return [
        { kind: 'subtotal', variant: 'designatedMale',   label: 'Designated Male',   data: designatedMale },
        ...emitDemographicRows(designatedMaleSlots, rowMap),
        { kind: 'subtotal', variant: 'designatedFemale', label: 'Designated Female', data: designatedFemale },
        ...emitDemographicRows(designatedFemaleSlots, rowMap),
        { kind: 'subtotal', variant: 'totalDesignated', label: 'Total Designated',  data: totalDesignated },
        { kind: 'subtotal', variant: 'nonDesignated',   label: 'Non-Designated',    data: nonDesignated },
        ...emitDemographicRows(nonDesignatedSlots, rowMap),
    ];
}

export interface SeverityChipCounts {
    critical: number;
    high: number;
    medium: number;
    onTrack: number;
}

export function getSeverityCounts(occupationalLevel: DeviationsDto.PlanSummaryOccupationalLevelDto): SeverityChipCounts {
    const counts: SeverityChipCounts = { critical: 0, high: 0, medium: 0, onTrack: 0 };
    occupationalLevel.demographicRows.forEach(row => {
        const severity: SeverityLevel = computeRelativeSeverity(row.relativeGapPercentage);
        if (severity === SeverityLevel.Critical) {
            counts.critical++;
        } else if (severity === SeverityLevel.High) {
            counts.high++;
        } else if (severity === SeverityLevel.Medium) {
            counts.medium++;
        } else if (severity === SeverityLevel.OnTrack) {
            counts.onTrack++;
        }
    });
    return counts;
}

export function computeRelativeSeverity(relativeGapPercentage: number | null): SeverityLevel {
    if (relativeGapPercentage === null || relativeGapPercentage === undefined) return SeverityLevel.OnTrack;
    if (relativeGapPercentage < -25) return SeverityLevel.Critical;
    if (relativeGapPercentage <= -15) return SeverityLevel.High;
    if (relativeGapPercentage <= -5) return SeverityLevel.Medium;
    if (relativeGapPercentage <= 0) return SeverityLevel.OnTrack;
    return SeverityLevel.OverRepresented;
}

export function formatPercentage(value: number | null): string {
    if (value === null || value === undefined) return '—';
    return `${value.toFixed(2)}%`;
}

export function formatVarianceHeadcount(value: number): string {
    const sign: string = value > 0 ? '+' : '';
    return `${sign}${value}`;
}

export function formatVariancePercentage(value: number | null): string {
    if (value === null || value === undefined) return '—';
    const sign: string = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
}

export function formatPriorityRank(priorityRank: string | null): string {
    if (priorityRank === null || priorityRank === undefined) return '—';
    return priorityRank;
}

export function formatPeriodHeading(planYear: number, reportingPeriodStart: string, reportingPeriodEnd: string): string {
    const start: string = moment(reportingPeriodStart).format('D MMMM YYYY');
    const end: string = moment(reportingPeriodEnd).format('D MMMM YYYY');
    return `EE Plan Summary - Year ${planYear} (${start} - ${end})`;
}
